import React, { useEffect, useState, useMemo, useContext } from "react";
import API from "../../api/api";
import Swal from "sweetalert2";
import { FaCheckCircle, FaCog, FaEdit, FaEye, FaFileAlt, FaFilter, FaSearch, FaTrash, FaTrophy, FaUser } from "react-icons/fa";
import { resolveAssetUrl } from "../../api/config";
import Navbar from "../../components/Navbar";
import { AuthContext } from "../../context/AuthContext";
import ExcellenceAwardsPoster from "../../components/ExcellenceAwardsPoster";

const normalizeMobile = (value) => {
  if (!value) return "";
  let digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.substring(1);
  } else if (digits.length === 12 && digits.startsWith("91")) {
    digits = digits.substring(2);
  }
  return digits.substring(0, 10);
};

const AWARD_CATEGORIES = [
  "Best Para Coach",
  "Best Junior Para Sportsman",
  "Best Junior Para Sportswoman",
  "Best Sub-Junior Para Sportsman",
  "Best Sub-Junior Para Sportswoman",
  "Best Senior Para Sportsman",
  "Best Senior Para Sportswoman",
  "Best International Para Athlete",
  "Best National Para Athlete",
  "Best Emerging Para Athlete",
  "Best Para Team",
  "Best District Para Sports Association",
  "Best Para Sports NGO / Organization",
  "Best Technical Official / Referee",
  "Best Classifier",
  "Best Support Staff (Physiotherapist, Trainer, Volunteer, Team Manager, etc.)",
  "Lifetime Achievement Award for Outstanding Contribution to Para Sports",
  "Special Jury Award for Exceptional Service to Para Sports",
  "Best Para Escort",
  "Best Para Guide Runner"
];

const APPLICANT_TYPES = {
  player: "Para Athlete",
  coach: "Coach",
  technical_official: "Technical Official",
  classifier: "Classifier",
  support_staff: "Support Staff",
  association: "District Association",
  ngo: "NGO",
  educational_institution: "Educational Institution",
  individual_organization: "Individual / Org"
};

const isImageUrl = (path) => {
  if (!path) return false;
  const cleanPath = path.split('?')[0].toLowerCase();
  return cleanPath.endsWith('.jpg') || 
         cleanPath.endsWith('.jpeg') || 
         cleanPath.endsWith('.png') || 
         cleanPath.endsWith('.webp') || 
         cleanPath.endsWith('.gif') ||
         cleanPath.endsWith('.bmp');
};

const getDetailsPhotoUrl = (app) => {
  if (!app) return "";
  if (isImageUrl(app.photo_path)) return app.photo_path;
  if (isImageUrl(app.achievements?.photo)) return app.achievements.photo;
  if (isImageUrl(app.registered_player?.photo)) return app.registered_player.photo;
  return "";
};

const getDynamicTemplates = (app) => {
  if (!app) return [];
  const tmpls = [];

  // Check missing database fields/files
  if (!app.photo_path && !app.achievements?.photo) {
    tmpls.push({ label: "Missing Photo", text: "Please upload a clear passport-size photo." });
  }
  if (!app.aadhaar_document_path) {
    tmpls.push({ label: "Missing Aadhaar Doc", text: "Please upload a clear scanned copy of your Aadhaar Card (Front and Back)." });
  }
  if (!app.aadhaar_number) {
    tmpls.push({ label: "Missing Aadhaar Number", text: "Please provide your 12-digit Aadhaar Card Number." });
  }
  if (app.applicant_type === "player" && !app.dob_proof_path) {
    tmpls.push({ label: "Missing DOB Proof", text: "Please upload your Date of Birth Proof (DOB Certificate / Class 10 Certificate) for age verification." });
  }
  if (!app.address) {
    tmpls.push({ label: "Missing Address", text: "Please fill in your full postal address." });
  }
  if (!app.district) {
    tmpls.push({ label: "Missing District", text: "Please select/provide your district." });
  }
  if (app.applicant_type === "association" && !app.district_president_secretary_name) {
    tmpls.push({ label: "Missing President/Secretary", text: "Please provide the name of the District President or Secretary." });
  }

  // Category-specific additional documents templates
  if (app.applicant_type === "player") {
    tmpls.push({ label: "Request Medal Certificates", text: "Please upload clear scanned copies of the player medal certificates for the achievements listed." });
    tmpls.push({ label: "Request District Attestation", text: "Please submit documents attested by the District Association to make your verification more effective." });
  } else if (app.applicant_type === "coach") {
    tmpls.push({ label: "Request Coach Details", text: "Please submit document proofs detailing your trained players' achievements and achievement history." });
  } else if (app.applicant_type === "ngo") {
    tmpls.push({ label: "Request NGO Supporting Docs", text: "Please submit the supporting documents and details of the players you have supported." });
  } else if (app.applicant_type === "association") {
    tmpls.push({ label: "Request Association Players", text: "Please submit the list of players registered under your association along with their supporting documents." });
  }

  // General fallback
  tmpls.push({ label: "General Request", text: "Please upload the requested supporting documents to verify your application." });

  return tmpls;
};

const ManageExcellenceAwards = () => {
  const { role, user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("applications");
  const [applications, setApplications] = useState([]);
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPosterModal, setShowPosterModal] = useState(false);

  // Filters State
  const [filterYear, setFilterYear] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Details Modal
  const [selectedApp, setSelectedApp] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Settings Form
  const [settingsForm, setSettingsForm] = useState({
    year: new Date().getFullYear().toString(),
    deadline_date: "",
    deadline_time: "23:59:00",
    ceremony_date: "",
    venue: "",
    contact_email: "",
    contact_phone: "",
    contact_name: "",
    is_active: true,
    is_open: true,
    extra_category_counts: {},
  });

  // Rankings State
  const [rankings, setRankings] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(false);
  const [rankingsEditable, setRankingsEditable] = useState(false);
  const [rankInputs, setRankInputs] = useState({}); // map: { [appId]: rankVal }
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [savingRankings, setSavingRankings] = useState(false);

  // Bulk selection states
  const [selectedAppIds, setSelectedAppIds] = useState([]);
  const [bulkRequestModalOpen, setBulkRequestModalOpen] = useState(false);
  const [bulkCustomMessage, setBulkCustomMessage] = useState("Please upload the requested documents in a proper high-quality scanned format.");
  const [bulkInjectInstructions, setBulkInjectInstructions] = useState(true);
  const [bulkFontFamily, setBulkFontFamily] = useState("sans-serif");
  const [bulkFontSize, setBulkFontSize] = useState("14px");
  const [bulkFontWeight, setBulkFontWeight] = useState("normal");
  const [bulkFontColor, setBulkFontColor] = useState("#334155");
  const [submittingBulkRequest, setSubmittingBulkRequest] = useState(false);

  const handleToggleSelectAll = () => {
    if (selectedAppIds.length === applications.length && applications.length > 0) {
      setSelectedAppIds([]);
    } else {
      setSelectedAppIds(applications.map(app => app.id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedAppIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSendBulkRequest = async () => {
    if (selectedAppIds.length === 0) {
      Swal.fire("Warning", "Please select at least one application to request documents.", "warning");
      return;
    }

    if (!bulkCustomMessage.trim()) {
      Swal.fire("Error", "Custom message is required.", "error");
      return;
    }

    try {
      setSubmittingBulkRequest(true);
      const payload = {
        ids: selectedAppIds,
        custom_message: bulkCustomMessage,
        inject_type_instructions: bulkInjectInstructions,
        font_family: bulkFontFamily,
        font_size: bulkFontSize,
        font_weight: bulkFontWeight,
        font_color: bulkFontColor,
      };

      const res = await API.bulkRequestExcellenceDocs(payload);
      if (res.success) {
        Swal.fire(
          "Success!",
          `Successfully processed ${res.total_processed} applications. ${res.emails_sent} email notifications sent.`,
          "success"
        );
        setSelectedAppIds([]);
        setBulkRequestModalOpen(false);
        loadApplications();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || err.message || "Failed to submit bulk request.", "error");
    } finally {
      setSubmittingBulkRequest(false);
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterYear) params.append("year", filterYear);
      if (filterType) params.append("applicant_type", filterType);
      if (filterCategory) params.append("award_category", filterCategory);
      if (filterStatus) params.append("status", filterStatus);
      if (searchQuery) params.append("search", searchQuery);

      const res = await API.get(`/admin/excellence-awards/applications?${params.toString()}`);
      setApplications(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load applications", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await API.get("/admin/excellence-awards/settings");
      setSettings(Array.isArray(res) ? res : []);
      if (res.length > 0) {
        const active = res.find(s => s.is_active) || res[0];
        let catCounts = {};
        try {
          catCounts = typeof active.extra_category_counts === "string"
            ? JSON.parse(active.extra_category_counts)
            : (active.extra_category_counts || {});
        } catch (e) {
          console.error("Failed to parse category counts:", e);
        }
        setSettingsForm({
          year: active.year,
          deadline_date: active.deadline_date,
          deadline_time: active.deadline_time || "23:59:00",
          ceremony_date: active.ceremony_date,
          venue: active.venue,
          contact_email: active.contact_email,
          contact_phone: active.contact_phone,
          contact_name: active.contact_name,
          is_active: active.is_active === 1 || active.is_active === true,
          is_open: active.is_open === 1 || active.is_open === true,
          extra_category_counts: catCounts,
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadRankings = async () => {
    try {
      setLoadingRankings(true);
      const params = new URLSearchParams();
      if (filterYear) params.append("year", filterYear);
      if (filterType) params.append("applicant_type", filterType);
      if (filterCategory) params.append("award_category", filterCategory);

      const res = await API.get(`/admin/excellence-awards/rankings?${params.toString()}`);
      const apps = Array.isArray(res) ? res : [];
      setRankings(apps);
      
      const inputs = {};
      apps.forEach((app) => {
        inputs[app.id] = app.ranking !== null && app.ranking !== undefined ? app.ranking : "";
      });
      setRankInputs(inputs);
      setRankingsEditable(false);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load rankings", "error");
    } finally {
      setLoadingRankings(false);
    }
  };

  useEffect(() => {
    if (activeTab === "applications") {
      loadApplications();
    } else if (activeTab === "rankings") {
      loadRankings();
    } else {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterYear, filterType, filterCategory, filterStatus]);

  const handleRankChange = (appId, val) => {
    const num = val === "" ? "" : parseInt(val) || "";
    setRankInputs(prev => ({ ...prev, [appId]: num }));
  };

  const handleRequestRankingsOtp = async () => {
    const hasExistingRankings = rankings.some(r => r.ranking !== null && r.ranking !== undefined);
    if (!hasExistingRankings) {
      setRankingsEditable(true);
      return;
    }

    try {
      setSendingOtp(true);
      const res = await API.sendExcellenceRankingsOtp();
      if (res.success) {
        setOtpCode("");
        setOtpModalOpen(true);
        Swal.fire("OTP Sent!", `A verification OTP has been sent to your email: ${user?.email}`, "success");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || err.message || "Failed to send verification OTP", "error");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtpAndUnlock = () => {
    if (!otpCode || otpCode.length !== 6) {
      Swal.fire("Error", "Please enter a valid 6-digit OTP code.", "error");
      return;
    }
    setRankingsEditable(true);
    setOtpModalOpen(false);
    Swal.fire("Unlocked!", "Rankings are now editable. Don't forget to save your changes.", "success");
  };

  const handleSaveRankings = async () => {
    try {
      setSavingRankings(true);
      const payload = {
        rankings: Object.entries(rankInputs).map(([id, ranking]) => ({
          id: parseInt(id),
          ranking: ranking === "" ? null : parseInt(ranking)
        })),
        otp: rankingsEditable ? otpCode : null
      };

      const res = await API.saveExcellenceRankings(payload);
      if (res.success) {
        Swal.fire("Saved!", "Excellence awards rankings updated successfully.", "success");
        setRankingsEditable(false);
        setOtpCode("");
        loadRankings();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || err.message || "Failed to save rankings", "error");
    } finally {
      setSavingRankings(false);
    }
  };

  const handleExportRankings = async (format) => {
    const params = new URLSearchParams();
    if (filterYear) params.append("year", filterYear);
    if (filterType) params.append("applicant_type", filterType);
    if (filterCategory) params.append("award_category", filterCategory);
    
    Swal.fire({
      title: "Generating Report...",
      text: "Please wait while we generate the download.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    await API.downloadExcellenceRankingsReport(format, params);
    Swal.close();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadApplications();
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await API.post("/admin/excellence-awards/settings", settingsForm);
      if (res.success) {
        Swal.fire("Saved!", "Yearly award settings saved successfully.", "success");
        loadSettings();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to save settings", "error");
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      const res = await API.put(`/admin/excellence-awards/applications/${id}/status`, { status });
      if (res.success) {
        Swal.fire("Updated!", `Application status set to ${status}.`, "success");
        if (selectedApp) {
          setSelectedApp({ ...selectedApp, status });
        }
        loadApplications();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const handleAcceptAchievement = async (appId, index) => {
    try {
      const res = await API.post(`/admin/excellence-awards/applications/${appId}/accept-achievement`, {
        achievement_index: index
      });
      if (res.success) {
        Swal.fire("Imported!", "Achievement successfully imported to Player Profile.", "success");
        
        // Update local achievements list status in modal
        if (selectedApp) {
          const updatedAchievements = { ...selectedApp.achievements };
          updatedAchievements.list[index].status = "accepted";
          setSelectedApp({ ...selectedApp, achievements: updatedAchievements });
        }
        loadApplications();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Failed to import achievement", "error");
    }
  };

  const handleDeleteApplication = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this application!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await API.delete(`/admin/excellence-awards/applications/${id}`);
          if (res.success || res.message) {
            Swal.fire("Deleted!", "Application has been deleted.", "success");
            loadApplications();
          } else {
            Swal.fire("Error", res.message || "Failed to delete application", "error");
          }
        } catch (err) {
          console.error(err);
          Swal.fire("Error", err.response?.data?.message || "Failed to delete application", "error");
        }
      }
    });
  };

  const [requestedDocsDesc, setRequestedDocsDesc] = useState("");
  const [requestingDocs, setRequestingDocs] = useState(false);
  const [expandedMedia, setExpandedMedia] = useState(null);
  const [reuploadDocs, setReuploadDocs] = useState({
    photo: false,
    aadhaar_document: false,
    dob_proof: false,
    main_document: false
  });

  // Application details editing state
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    mobile: "",
    email: "",
    district: "",
    address: ""
  });
  const [savingDetails, setSavingDetails] = useState(false);

  const handleAppendTemplate = (text) => {
    setRequestedDocsDesc((prev) => {
      const cleanPrev = String(prev || "").trim();
      if (!cleanPrev) {
        return `- ${text}`;
      }
      if (cleanPrev.includes(text)) return cleanPrev;
      
      let formattedPrev = cleanPrev;
      if (!formattedPrev.startsWith("- ")) {
        formattedPrev = `- ${formattedPrev}`;
      }
      return `${formattedPrev}\n- ${text}`;
    });
  };

  const handleRequestDocs = async (id) => {
    if (!requestedDocsDesc) {
      Swal.fire("Error", "Please enter a description of the requested documents", "error");
      return;
    }
    const requestedReuploads = Object.keys(reuploadDocs).filter(k => reuploadDocs[k]);
    setRequestingDocs(true);
    try {
      const res = await API.requestExcellenceAdditionalDocs(id, {
        requested_documents_desc: requestedDocsDesc,
        requested_reuploads: requestedReuploads
      });
      if (res.success) {
        Swal.fire("Requested!", "Request has been logged and applicant notified.", "success");
        setSelectedApp({
          ...selectedApp,
          requested_documents_desc: requestedDocsDesc,
          reupload_requested_docs: requestedReuploads,
          additional_documents_status: "requested"
        });
        setRequestedDocsDesc("");
        loadApplications();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Failed to request documents", "error");
    } finally {
      setRequestingDocs(false);
    }
  };

  const handleRequestPhoto = async (id) => {
    setRequestingDocs(true);
    try {
      const res = await API.requestExcellenceAdditionalDocs(id, {
        requested_documents_desc: "Please upload a clear passport-size photo.",
        requested_reuploads: ["photo"]
      });
      if (res.success) {
        Swal.fire("Requested!", "Photo request has been sent and applicant notified.", "success");
        setSelectedApp({
          ...selectedApp,
          requested_documents_desc: "Please upload a clear passport-size photo.",
          reupload_requested_docs: ["photo"],
          additional_documents_status: "requested"
        });
        setReuploadDocs(prev => ({ ...prev, photo: true }));
        loadApplications();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Failed to request photo", "error");
    } finally {
      setRequestingDocs(false);
    }
  };

  const startEditingDetails = () => {
    setEditForm({
      name: selectedApp.name || "",
      mobile: selectedApp.mobile || "",
      email: selectedApp.email || "",
      district: selectedApp.district || "",
      address: selectedApp.address || ""
    });
    setIsEditingDetails(true);
  };

  const handleSaveDetails = async () => {
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(editForm.mobile)) {
      Swal.fire("Error", "Please enter a valid 10-digit mobile number.", "error");
      return;
    }
    if (!editForm.name.trim()) {
      Swal.fire("Error", "Name is required.", "error");
      return;
    }

    setSavingDetails(true);
    try {
      const res = await API.put(`/excellence-awards/applications/${selectedApp.id}/details`, editForm);
      if (res.success) {
        Swal.fire("Success!", "Application details updated successfully.", "success");
        setSelectedApp({
          ...selectedApp,
          ...editForm
        });
        setIsEditingDetails(false);
        loadApplications();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Failed to update details", "error");
    } finally {
      setSavingDetails(false);
    }
  };

  const typeCounts = useMemo(() => {
    const counts = {};
    applications.forEach((app) => {
      counts[app.applicant_type] = (counts[app.applicant_type] || 0) + 1;
    });
    return counts;
  }, [applications]);

  const openAppDetails = async (app) => {
    try {
      const res = await API.get(`/admin/excellence-awards/applications/${app.id}`);
      const fullApp = res?.data || res || app;
      setSelectedApp(fullApp);
      
      let currentlyRequested = [];
      if (Array.isArray(fullApp.reupload_requested_docs)) {
        currentlyRequested = fullApp.reupload_requested_docs;
      } else if (typeof fullApp.reupload_requested_docs === 'string') {
        try {
          currentlyRequested = JSON.parse(fullApp.reupload_requested_docs);
        } catch (e) {
          currentlyRequested = [];
        }
      }
      if (!Array.isArray(currentlyRequested)) {
        currentlyRequested = [];
      }

      setReuploadDocs({
        photo: currentlyRequested.includes("photo"),
        aadhaar_document: currentlyRequested.includes("aadhaar_document"),
        dob_proof: currentlyRequested.includes("dob_proof"),
        main_document: currentlyRequested.includes("main_document")
      });
      setShowModal(true);
    } catch (err) {
      console.error("Failed to fetch full application details", err);
      setSelectedApp(app);
      setShowModal(true);
    }
  };

  const closeAppDetails = () => {
    setSelectedApp(null);
    setShowModal(false);
    setIsEditingDetails(false);
    setReuploadDocs({
      photo: false,
      aadhaar_document: false,
      dob_proof: false,
      main_document: false
    });
    setExpandedMedia(null);
  };

  const getAdditionalDocs = (app) => {
    if (!app || !app.additional_documents) return [];
    if (Array.isArray(app.additional_documents)) return app.additional_documents;
    if (typeof app.additional_documents === 'string') {
      try {
        return JSON.parse(app.additional_documents);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  return (
    <>
      <Navbar />
      <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2">
            <FaTrophy /> Excellence Awards Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">Review applications, configure dates, and view document proofs.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPosterModal(true)}
            className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-xl text-xs font-black transition-all flex items-center gap-2 shadow-md hover:scale-105 active:scale-95 duration-150"
          >
            <FaTrophy /> Download Poster
          </button>

          <div className="flex bg-slate-100 p-1.5 rounded-xl border">
            <button
              onClick={() => setActiveTab("applications")}
              className={`px-4 py-2 rounded-lg font-bold text-xs transition-all ${
                activeTab === "applications" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-950"
              }`}
            >
              Applications ({applications.length})
            </button>
            <button
              onClick={() => setActiveTab("rankings")}
              className={`px-4 py-2 rounded-lg font-bold text-xs transition-all ${
                activeTab === "rankings" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-950"
              }`}
            >
              Manage Rankings
            </button>
            <button
              onClick={() => setActiveTab("settings")}
              className={`px-4 py-2 rounded-lg font-bold text-xs transition-all ${
                activeTab === "settings" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-950"
              }`}
            >
              Yearly Settings
            </button>
          </div>
        </div>
      </div>

      {activeTab === "applications" ? (
        <div className="space-y-6 text-left">
          
          {/* Summary Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {Object.entries(APPLICANT_TYPES).map(([type, label]) => {
              const count = typeCounts[type] || 0;
              return (
                <div key={type} className="bg-white border rounded-2xl p-4 text-center shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
                  <span className="block text-[10px] uppercase font-bold text-slate-400 leading-tight">{label}</span>
                  <span className="block text-2xl font-black text-slate-900 mt-2">{count}</span>
                </div>
              );
            })}
          </div>

          {/* FILTERS */}
          <div className="bg-white border rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 text-slate-900 font-extrabold text-sm border-b pb-2 mb-2">
              <FaFilter className="text-slate-400" /> Filters
            </div>
            
            <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Year</label>
                <select
                  className="w-full border rounded-xl px-3 py-2 text-xs focus:outline-none text-slate-700"
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                >
                  <option value="">All Years</option>
                  {[...new Set([...settings.map(s => s.year), 2026])].filter(Boolean).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Type</label>
                <select
                  className="w-full border rounded-xl px-3 py-2 text-xs focus:outline-none text-slate-700"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All Types</option>
                  {Object.entries(APPLICANT_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Category</label>
                <select
                  className="w-full border rounded-xl px-3 py-2 text-xs focus:outline-none text-slate-700"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {AWARD_CATEGORIES.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Status</label>
                <select
                  className="w-full border rounded-xl px-3 py-2 text-xs focus:outline-none text-slate-700"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Search Keyword</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Search name, phone..."
                    className="w-full border rounded-xl px-3 py-2 text-xs focus:outline-none text-slate-700"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="px-3 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800"
                  >
                    <FaSearch />
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* BULK ACTION BAR */}
          {selectedAppIds.length > 0 && (
            <div className="flex justify-between items-center bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-md border border-slate-950">
              <span className="text-xs font-bold">
                Selected: <strong className="text-amber-400 font-extrabold">{selectedAppIds.length}</strong> application(s)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedAppIds([])}
                  className="px-3.5 py-1.5 border border-slate-700 hover:bg-slate-800 rounded-xl text-xs font-semibold text-slate-300 transition-all"
                >
                  Clear Selection
                </button>
                <button
                  onClick={() => setBulkRequestModalOpen(true)}
                  className="px-4 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-slate-950 rounded-xl text-xs font-black shadow-sm transition-all animate-pulse"
                >
                  📩 Request Documents
                </button>
              </div>
            </div>
          )}

          {/* APPLICATIONS TABLE */}
          {loading ? (
            <div className="text-center py-12 text-slate-500 font-semibold animate-pulse text-lg">Loading applications...</div>
          ) : applications.length === 0 ? (
            <div className="bg-white border rounded-2xl p-12 text-center text-slate-500 shadow-sm">
              <FaTrophy size={48} className="mx-auto text-slate-200 mb-3" />
              <p className="font-bold text-lg">No Excellence Award Applications found</p>
              <p className="text-xs text-slate-400 mt-1">Adjust filters or search criteria.</p>
            </div>
          ) : (
            <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">
                      <th className="px-6 py-4" style={{ width: "40px" }}>
                        <input
                          type="checkbox"
                          className="rounded text-slate-900 border-gray-300 focus:ring-slate-900 cursor-pointer"
                          checked={applications.length > 0 && selectedAppIds.length === applications.length}
                          onChange={handleToggleSelectAll}
                        />
                      </th>
                      <th className="px-6 py-4">Year</th>
                      <th className="px-6 py-4">Photo</th>
                      <th className="px-6 py-4">Applicant Name</th>
                      <th className="px-6 py-4">Applicant Type</th>
                      <th className="px-6 py-4">Gender</th>
                      <th className="px-6 py-4">Registered Player?</th>
                      <th className="px-6 py-4">Award Category</th>
                      <th className="px-6 py-4">District</th>
                      <th className="px-6 py-4">Mobile</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-xs divide-y divide-slate-100 text-slate-700">
                    {applications.map((app) => (
                      <tr key={app.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            className="rounded text-slate-900 border-gray-300 focus:ring-slate-900 cursor-pointer"
                            checked={selectedAppIds.includes(app.id)}
                            onChange={() => handleToggleSelectRow(app.id)}
                          />
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-900">{app.year}</td>
                        <td className="px-6 py-4">
                          {isImageUrl(app.photo_path || app.achievements?.photo) ? (
                            <img
                              src={resolveAssetUrl(app.photo_path || app.achievements.photo)}
                              alt="Applicant"
                              className="w-10 h-10 rounded-lg object-cover border cursor-pointer hover:scale-105 transition-transform"
                              onClick={() => setExpandedMedia({ url: resolveAssetUrl(app.photo_path || app.achievements.photo), type: 'image' })}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-slate-100 border flex items-center justify-center text-[10px] font-semibold text-slate-400">
                              No Pic
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-bold text-slate-900">{app.name}</div>
                          {app.email && <div className="text-slate-400 text-[10px]">{app.email}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-[10px] font-semibold border">
                            {APPLICANT_TYPES[app.applicant_type] || app.applicant_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-semibold capitalize text-slate-650">
                          {app.gender || "N/A"}
                        </td>
                        <td className="px-6 py-4">
                          {app.is_registered ? (
                            <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-[10px] font-black border border-emerald-250" title={app.registration_id}>
                              Yes ({app.registration_id})
                            </span>
                          ) : (
                            <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold border border-blue-200">
                              New
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 max-w-[200px] truncate">{app.award_category}</td>
                        <td className="px-6 py-4 font-bold text-slate-800">{app.district || "N/A"}</td>
                        <td className="px-6 py-4 font-semibold">{app.mobile}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider border ${
                            app.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                            app.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                            "bg-amber-50 text-amber-700 border-amber-200"
                          }`}>
                            {app.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                          <button
                            onClick={() => openAppDetails(app)}
                            className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition-all shadow-sm shrink-0"
                          >
                            <FaEye /> View
                          </button>
                          {role === "admin" && (
                            <button
                              onClick={() => handleDeleteApplication(app.id)}
                              className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition-all shadow-sm shrink-0"
                            >
                              <FaTrash /> Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : activeTab === "rankings" ? (
        <div className="space-y-6 text-left">
          {/* Header Controls for Rankings */}
          <div className="bg-white border rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-slate-900 font-extrabold text-sm flex items-center gap-2 border-b pb-2 mb-2 w-full sm:w-auto">
                <FaTrophy className="text-emerald-600" /> Filter Approved Applications
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleExportRankings("pdf")}
                  className="px-3.5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                  disabled={rankings.length === 0}
                >
                  📄 Export PDF
                </button>
                <button
                  onClick={() => handleExportRankings("excel")}
                  className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                  disabled={rankings.length === 0}
                >
                  📊 Export Excel
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Award Year</label>
                <select
                  className="w-full border rounded-xl px-3 py-2 text-xs focus:outline-none text-slate-700"
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                >
                  <option value="">Select Year</option>
                  {[...new Set([...settings.map(s => s.year), 2026])].filter(Boolean).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Applicant Type</label>
                <select
                  className="w-full border rounded-xl px-3 py-2 text-xs focus:outline-none text-slate-700"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All Types</option>
                  {Object.entries(APPLICANT_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Award Category</label>
                <select
                  className="w-full border rounded-xl px-3 py-2 text-xs focus:outline-none text-slate-700"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {AWARD_CATEGORIES.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* RANKINGS DATA TABLE */}
          {loadingRankings ? (
            <div className="text-center py-12 text-slate-500 font-semibold animate-pulse text-lg">Loading approved applicants...</div>
          ) : rankings.length === 0 ? (
            <div className="bg-white border rounded-2xl p-12 text-center text-slate-500 shadow-sm">
              <FaTrophy size={48} className="mx-auto text-slate-200 mb-3" />
              <p className="font-bold text-lg">No Approved Applications found</p>
              <p className="text-xs text-slate-400 mt-1">Adjust filters or year. Make sure applications are first approved.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Toolbar */}
              <div className="flex justify-between items-center bg-white p-4 border rounded-2xl shadow-sm">
                <span className="text-xs font-bold text-slate-500">
                  Approved Applicants: <strong className="text-slate-800">{rankings.length}</strong>
                </span>
                <div className="flex gap-2">
                  {!rankingsEditable ? (
                    <button
                      onClick={handleRequestRankingsOtp}
                      disabled={sendingOtp}
                      className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-extrabold shadow-sm transition-all"
                    >
                      {sendingOtp ? "Requesting..." : "🔓 Edit Rankings"}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setRankingsEditable(false);
                          const inputs = {};
                          rankings.forEach(r => { inputs[r.id] = r.ranking !== null ? r.ranking : ""; });
                          setRankInputs(inputs);
                        }}
                        className="px-4 py-2 border rounded-xl hover:bg-slate-100 text-xs font-bold text-slate-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveRankings}
                        disabled={savingRankings}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black shadow-sm transition-all"
                      >
                        {savingRankings ? "Saving..." : "💾 Save Rankings"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">
                        <th className="px-6 py-4" style={{ width: "120px" }}>Manual Rank</th>
                        <th className="px-6 py-4">Applicant Name</th>
                        <th className="px-6 py-4">Applicant Type</th>
                        <th className="px-6 py-4">Award Category</th>
                        <th className="px-6 py-4">District</th>
                        <th className="px-6 py-4">Mobile</th>
                      </tr>
                    </thead>
                    <tbody className="text-xs divide-y divide-slate-100 text-slate-700">
                      {rankings.map((app) => (
                        <tr key={app.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <input
                              type="number"
                              min="1"
                              disabled={!rankingsEditable}
                              placeholder="N/A"
                              value={rankInputs[app.id] ?? ""}
                              onChange={(e) => handleRankChange(app.id, e.target.value)}
                              className={`w-20 border rounded-lg px-2 py-1 text-xs font-bold text-center focus:outline-none ${
                                rankingsEditable 
                                  ? "bg-white border-slate-300 text-slate-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" 
                                  : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed"
                              }`}
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-bold text-slate-900">{app.name}</div>
                            {app.email && <div className="text-slate-400 text-[10px]">{app.email}</div>}
                          </td>
                          <td className="px-6 py-4">
                            <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-[10px] font-semibold border">
                              {app.applicant_type_label}
                            </span>
                          </td>
                          <td className="px-6 py-4 truncate max-w-[200px]">{app.award_category}</td>
                          <td className="px-6 py-4 font-bold text-slate-800">{app.district || "N/A"}</td>
                          <td className="px-6 py-4 font-semibold">{app.mobile}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        
        /* SETTINGS TAB */
        <div className="bg-white border rounded-2xl p-6 shadow-sm max-w-4xl">
          <div className="flex items-center gap-2 border-b pb-3 mb-6 text-slate-900 font-extrabold text-lg">
            <FaCog className="text-slate-400" /> Yearly Award Timeline & Settings
          </div>

          <form onSubmit={handleSettingsSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Award Year *</label>
                <input
                  type="number"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none text-slate-700 focus:border-slate-900"
                  value={settingsForm.year}
                  onChange={(e) => setSettingsForm({ ...settingsForm, year: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Active Status</label>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={settingsForm.is_active}
                    onChange={(e) => setSettingsForm({ ...settingsForm, is_active: e.target.checked })}
                    className="h-4 w-4 text-slate-900 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="text-xs font-semibold text-slate-700">Set Active Year</label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Application Status</label>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    id="is_open"
                    checked={settingsForm.is_open}
                    onChange={(e) => setSettingsForm({ ...settingsForm, is_open: e.target.checked })}
                    className="h-4 w-4 text-slate-900 border-gray-300 rounded"
                  />
                  <label htmlFor="is_open" className="text-xs font-semibold text-slate-700">Allow Applications (Open)</label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Application Deadline Date *</label>
                <input
                  type="date"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none text-slate-700 focus:border-slate-900"
                  value={settingsForm.deadline_date}
                  onChange={(e) => setSettingsForm({ ...settingsForm, deadline_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Application Deadline Time *</label>
                <input
                  type="time"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none text-slate-700 focus:border-slate-900"
                  value={settingsForm.deadline_time}
                  onChange={(e) => setSettingsForm({ ...settingsForm, deadline_time: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Awards Ceremony Date *</label>
                <input
                  type="date"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none text-slate-700 focus:border-slate-900"
                  value={settingsForm.ceremony_date}
                  onChange={(e) => setSettingsForm({ ...settingsForm, ceremony_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Ceremony Venue *</label>
              <input
                type="text"
                className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none text-slate-700 focus:border-slate-900"
                value={settingsForm.venue}
                onChange={(e) => setSettingsForm({ ...settingsForm, venue: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t pt-4">
              <div className="col-span-3">
                <h4 className="text-xs font-extrabold uppercase text-slate-400 mb-2">Secretary / Contact Info</h4>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Secretary Name *</label>
                <input
                  type="text"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none text-slate-700 focus:border-slate-900"
                  value={settingsForm.contact_name}
                  onChange={(e) => setSettingsForm({ ...settingsForm, contact_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Contact Phone *</label>
                <input
                  type="text"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none text-slate-700 focus:border-slate-900"
                  value={settingsForm.contact_phone}
                  onChange={(e) => setSettingsForm({ ...settingsForm, contact_phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Contact Email *</label>
                <input
                  type="email"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none text-slate-700 focus:border-slate-900"
                  value={settingsForm.contact_email}
                  onChange={(e) => setSettingsForm({ ...settingsForm, contact_email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-4">
              <div className="col-span-2">
                <h4 className="text-xs font-extrabold uppercase text-slate-400 mb-1">Extra Category Counts (Offset Values)</h4>
                <p className="text-[11px] text-slate-400 font-medium">Add extra counts to manually increase individual award category display figures. These will automatically calculate and update the total applicant counts as well.</p>
              </div>

              <div className="col-span-2 max-h-[300px] overflow-y-auto pr-2 grid grid-cols-1 sm:grid-cols-2 gap-4 custom-scrollbar">
                {AWARD_CATEGORIES.map((cat, idx) => (
                  <div key={cat} className="flex flex-col gap-1 border-b pb-2 sm:pb-0 border-slate-100">
                    <label className="text-xs font-bold text-slate-650 line-clamp-1">{idx + 1}. {cat}</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full border rounded-xl px-4 py-1.5 text-xs focus:outline-none text-slate-700 focus:border-slate-900"
                      value={settingsForm.extra_category_counts?.[cat] || 0}
                      onChange={(e) => {
                        const nextCounts = { ...(settingsForm.extra_category_counts || {}) };
                        nextCounts[cat] = parseInt(e.target.value) || 0;
                        setSettingsForm({ ...settingsForm, extra_category_counts: nextCounts });
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowPosterModal(true)}
                className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-xl text-sm font-bold transition-all flex items-center gap-2"
              >
                <FaTrophy /> Download Poster
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-bold transition-all"
              >
                Save Settings
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ===== DETAIL MODAL ===== */}
      {showModal && selectedApp && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border w-full max-w-4xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            
            {/* Modal Header */}
            <div className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="font-extrabold text-lg flex items-center gap-2">
                  <FaTrophy /> Excellence Awards Application - {selectedApp.year}
                </h3>
                <p className="text-slate-400 text-xs mt-0.5">{selectedApp.award_category}</p>
              </div>
              <div className="flex items-center gap-4">
                {!isEditingDetails && (
                  <button
                    onClick={startEditingDetails}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5"
                  >
                    <FaEdit /> Edit Details
                  </button>
                )}
                <button
                  onClick={closeAppDetails}
                  className="text-slate-400 hover:text-white font-extrabold text-xl transition-colors"
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6 text-slate-700 text-xs">
              
              {/* Registered Player Profile Details Card */}
              {selectedApp.registered_player && (
                <div className="bg-emerald-50/40 border border-emerald-250/50 rounded-3xl p-5 text-left space-y-3">
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    {selectedApp.registered_player.photo && (
                      <div className="w-16 h-16 rounded-xl overflow-hidden border bg-slate-50 flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity">
                        <img
                          src={resolveAssetUrl(selectedApp.registered_player.photo)}
                          alt="Registered Profile"
                          className="w-full h-full object-cover"
                          onClick={() => setExpandedMedia({ url: resolveAssetUrl(selectedApp.registered_player.photo), type: 'image' })}
                        />
                      </div>
                    )}
                    <div className="flex-1 space-y-3">
                      <h4 className="text-sm font-black text-emerald-800 flex items-center gap-2">
                        <FaUser /> Registered Player Profile Details
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-emerald-600">Registration ID</span>
                          <strong className="text-slate-900">{selectedApp.registered_player.registration_id}</strong>
                        </div>
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-emerald-600">Full Name</span>
                          <span className="font-semibold text-slate-800">{selectedApp.registered_player.name}</span>
                        </div>
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-emerald-600">Mobile & Email</span>
                          <span className="font-semibold text-slate-800">{selectedApp.registered_player.mobile} / {selectedApp.registered_player.email || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-emerald-600">Aadhaar Number</span>
                          <span className="font-semibold text-slate-800">{selectedApp.registered_player.aadhaar_number || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-emerald-600">Registered District</span>
                          <span className="font-semibold text-slate-800">{selectedApp.registered_player.district}</span>
                        </div>
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-emerald-600">Disability Types</span>
                          <span className="font-semibold text-slate-800">
                            {Array.isArray(selectedApp.registered_player.disability_types)
                              ? selectedApp.registered_player.disability_types.join(", ")
                              : "None"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {selectedApp.registered_player.address && (
                    <div className="pt-2 border-t text-xs">
                      <span className="block text-[9px] uppercase font-bold text-emerald-600">Registered Address</span>
                      <p className="text-slate-700 mt-0.5">{selectedApp.registered_player.address}</p>
                    </div>
                  )}
                  {selectedApp.registered_player.sadarem && (
                    <div className="pt-2 flex gap-2">
                      <a
                        href={resolveAssetUrl(selectedApp.registered_player.sadarem)}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 border border-emerald-200 bg-white text-emerald-700 font-bold rounded-lg text-[10px] inline-flex items-center gap-1 hover:bg-emerald-50 transition-colors"
                      >
                        <FaFileAlt /> View SADAREM Certificate
                      </a>
                    </div>
                  )}
                </div>
              )}
              
              {/* Photo & Basic Details */}
              <div className="flex flex-col md:flex-row gap-6 border-b pb-6">
                <div className="flex flex-col items-center mx-auto md:mx-0 flex-shrink-0">
                  {getDetailsPhotoUrl(selectedApp) ? (
                    <div className="w-32 h-32 rounded-2xl overflow-hidden border bg-slate-50 cursor-pointer hover:opacity-90 transition-opacity">
                      <img
                        src={resolveAssetUrl(getDetailsPhotoUrl(selectedApp))}
                        alt="Applicant"
                        className="w-full h-full object-cover"
                        onClick={() => setExpandedMedia({ url: resolveAssetUrl(getDetailsPhotoUrl(selectedApp)), type: 'image' })}
                      />
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-2xl bg-slate-50 border flex flex-col items-center justify-center text-[10px] font-bold text-slate-400 gap-1">
                      <span>📸 No Photo</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRequestPhoto(selectedApp.id)}
                    className="mt-2 text-[10px] bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-300 font-extrabold px-2 py-1 rounded-lg transition-all"
                  >
                    Re-request Photo 🔄
                  </button>
                </div>
                
                {isEditingDetails ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 flex-1 text-left bg-slate-50 border p-5 rounded-2xl">
                    <div className="md:col-span-2">
                      <label className="block text-[10px] uppercase font-black text-slate-500 mb-1">Full Name *</label>
                      <input
                        type="text"
                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-slate-850 text-xs"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase font-black text-slate-500 mb-1">Mobile Number *</label>
                      <input
                        type="text"
                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-slate-850 text-xs"
                        value={editForm.mobile}
                        onChange={(e) => {
                          const val = normalizeMobile(e.target.value);
                          setEditForm({ ...editForm, mobile: val });
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase font-black text-slate-500 mb-1">Email Address</label>
                      <input
                        type="email"
                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-slate-850 text-xs"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase font-black text-slate-500 mb-1">District</label>
                      <input
                        type="text"
                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-slate-850 text-xs"
                        value={editForm.district}
                        onChange={(e) => setEditForm({ ...editForm, district: e.target.value })}
                      />
                    </div>
                    <div>
                      <span className="block text-[10px] uppercase font-black text-slate-400 mb-1">Applicant Type</span>
                      <span className="bg-slate-200 text-slate-700 px-2.5 py-1 rounded-lg font-bold border inline-block text-xs">
                        {APPLICANT_TYPES[selectedApp.applicant_type] || selectedApp.applicant_type}
                      </span>
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-[10px] uppercase font-black text-slate-500 mb-1">Address</label>
                      <textarea
                        rows={2}
                        className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:border-slate-850 text-xs"
                        value={editForm.address}
                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                        placeholder="Enter full address details..."
                      />
                    </div>
                    <div className="md:col-span-3 flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => setIsEditingDetails(false)}
                        className="px-4 py-2 border rounded-xl text-slate-600 hover:bg-slate-100 font-bold text-xs transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveDetails}
                        disabled={savingDetails}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-black text-xs transition-colors"
                      >
                        {savingDetails ? "Saving..." : "Save Details"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 flex-1 text-left">
                      <div>
                        <span className="block text-[9px] uppercase font-bold text-slate-400">Full Name</span>
                        <span className="font-bold text-slate-900 text-sm">{selectedApp.name}</span>
                      </div>
                      <div>
                        <span className="block text-[9px] uppercase font-bold text-slate-400">Applicant Status / Type</span>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                          <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-bold border text-[10px]">
                            {APPLICANT_TYPES[selectedApp.applicant_type] || selectedApp.applicant_type}
                          </span>
                          {selectedApp.is_registered_player ? (
                            <span className="bg-emerald-100 text-emerald-800 border border-emerald-250 px-2 py-0.5 rounded font-extrabold text-[9px]">
                              Registered Player
                            </span>
                          ) : (
                            <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded font-extrabold text-[9px]">
                              New (Unregistered)
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="block text-[9px] uppercase font-bold text-slate-400">Mobile Number</span>
                        <span className="font-bold text-slate-900">{selectedApp.mobile}</span>
                      </div>
                      <div>
                        <span className="block text-[9px] uppercase font-bold text-slate-400">Email Address</span>
                        <span className="font-semibold text-slate-800">{selectedApp.email || "N/A"}</span>
                      </div>
                      {selectedApp.relation_name && (
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-slate-400">Father / Guardian</span>
                          <span className="font-semibold text-slate-800">{selectedApp.relation_name}</span>
                        </div>
                      )}
                      {selectedApp.dob && (
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-slate-400">Date of Birth</span>
                          <span className="font-semibold text-slate-800">{new Date(selectedApp.dob).toLocaleDateString()}</span>
                        </div>
                      )}
                      {selectedApp.gender && (
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-slate-400">Gender</span>
                          <span className="font-semibold text-slate-800">{selectedApp.gender}</span>
                        </div>
                      )}
                      {selectedApp.experience_years && (
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-slate-400">Experience Years</span>
                          <span className="font-semibold text-slate-800">{selectedApp.experience_years} years</span>
                        </div>
                      )}
                      {selectedApp.aadhaar_number && (
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-slate-400">Aadhaar Number</span>
                          <span className="font-semibold text-slate-800">{selectedApp.aadhaar_number}</span>
                        </div>
                      )}
                      {selectedApp.district_president_secretary_name && (
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-slate-400">District President / Secretary</span>
                          <span className="font-semibold text-slate-800">{selectedApp.district_president_secretary_name}</span>
                        </div>
                      )}
                      {selectedApp.district && (
                        <div>
                          <span className="block text-[9px] uppercase font-bold text-slate-400">District & State</span>
                          <span className="font-semibold text-slate-800">{selectedApp.district}, {selectedApp.state || 'Andhra Pradesh'}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Address details */}
              {!isEditingDetails && selectedApp.address && (
                <div className="border-b pb-6 text-left">
                  <span className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Address</span>
                  <p className="bg-slate-50 border rounded-xl p-3 text-slate-700 leading-relaxed text-xs">
                    {selectedApp.address}
                  </p>
                </div>
              )}

              {/* Specific contributions/training text */}
              {(selectedApp.players_trained || selectedApp.contributions) && (
                <div className="grid grid-cols-1 gap-4 border-b pb-6 text-left">
                  {selectedApp.players_trained && (
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">Players Trained / Achievements</h4>
                      <p className="bg-slate-50 border rounded-xl p-3 text-slate-700 leading-relaxed whitespace-pre-line">
                        {selectedApp.players_trained}
                      </p>
                    </div>
                  )}
                  {selectedApp.contributions && (
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">Contributions to Para Sports</h4>
                      <p className="bg-slate-50 border rounded-xl p-3 text-slate-700 leading-relaxed whitespace-pre-line">
                        {selectedApp.contributions}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Mandatory Documents Uploaded */}
              <div className="border-b pb-6 text-left">
                <h4 className="font-bold text-slate-900 mb-3">Mandatory Documents Uploaded</h4>
                <div className="flex flex-wrap gap-3">
                  {selectedApp.aadhaar_document_path && (
                    <a
                      href={resolveAssetUrl(selectedApp.aadhaar_document_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 border border-slate-300 rounded-xl bg-slate-50 text-slate-700 font-bold inline-flex items-center gap-1.5 hover:bg-slate-100 transition-colors"
                    >
                      <FaFileAlt /> Aadhaar Card
                    </a>
                  )}
                  {selectedApp.dob_proof_path && (
                    <a
                      href={resolveAssetUrl(selectedApp.dob_proof_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 border border-slate-300 rounded-xl bg-slate-50 text-slate-700 font-bold inline-flex items-center gap-1.5 hover:bg-slate-100 transition-colors"
                    >
                      <FaFileAlt /> DOB Proof
                    </a>
                  )}
                  {selectedApp.achievements?.main_document && (
                    <a
                      href={resolveAssetUrl(selectedApp.achievements.main_document)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 border border-slate-300 rounded-xl bg-slate-50 text-slate-700 font-bold inline-flex items-center gap-1.5 hover:bg-slate-100 transition-colors"
                    >
                      <FaFileAlt /> Main Document
                    </a>
                  )}
                </div>
              </div>

              {/* Event Media Uploads */}
              {selectedApp.achievements?.event_media && selectedApp.achievements.event_media.length > 0 && (
                <div className="border-b pb-6 text-left">
                  <h4 className="font-bold text-slate-900 mb-3">Event Photos & Videos (Click to Preview)</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {selectedApp.achievements.event_media.map((path, idx) => {
                      const isVideo = path.endsWith('.mp4') || path.endsWith('.mov') || path.endsWith('.webm');
                      return (
                        <div 
                          key={idx} 
                          className="rounded-xl overflow-hidden border bg-slate-50 aspect-video relative flex flex-col justify-between shadow-sm cursor-pointer hover:opacity-95 transition-opacity"
                          onClick={() => setExpandedMedia({ url: resolveAssetUrl(path), type: isVideo ? 'video' : 'image' })}
                        >
                          {isVideo ? (
                            <video src={resolveAssetUrl(path)} className="w-full h-full object-cover pointer-events-none" />
                          ) : (
                            <img src={resolveAssetUrl(path)} alt="Event media" className="w-full h-full object-cover" />
                          )}
                          <span className="bg-slate-900/80 hover:bg-slate-900 text-white text-[9px] font-bold text-center py-1 select-none">
                            View Preview
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Additional Requested Documents Section */}
              <div className="border-b pb-6 text-left space-y-4">
                <h4 className="font-bold text-slate-900">Request Supporting Documents</h4>
                
                {selectedApp.additional_documents_status && selectedApp.additional_documents_status !== 'none' && (
                  <div className="bg-slate-50 border rounded-2xl p-4 space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-slate-600">Status:</span>
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider border ${
                        selectedApp.additional_documents_status === "requested" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        "bg-emerald-50 text-emerald-700 border-emerald-200"
                      }`}>
                        {selectedApp.additional_documents_status}
                      </span>
                    </div>
                    {selectedApp.requested_documents_desc && (
                      <div className="text-xs">
                        <strong className="text-slate-600 block">Requested Items:</strong>
                        <p className="italic text-slate-500 mt-0.5">"{selectedApp.requested_documents_desc}"</p>
                      </div>
                    )}

                    {/* Display submitted additional docs */}
                    {getAdditionalDocs(selectedApp).length > 0 && (
                      <div className="space-y-1.5 pt-2 border-t mt-2">
                        <strong className="text-slate-600 block mb-1">Submitted Additional Documents:</strong>
                        {getAdditionalDocs(selectedApp).map((doc, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-white p-2 rounded-lg border text-xs">
                            <span className="truncate font-semibold text-slate-700 max-w-[200px]">{doc.original_name || 'Additional Document'}</span>
                            <a
                              href={resolveAssetUrl(doc.file_path)}
                              target="_blank"
                              rel="noreferrer"
                              className="px-2 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded text-[10px] font-bold"
                            >
                              View
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Form to Request Docs */}
                <div className="flex flex-col gap-2 mt-2 bg-slate-50 p-4 rounded-2xl border">
                  <label className="block text-[10px] uppercase font-extrabold text-slate-500">
                    Request Additional/Missing Documents
                  </label>
                  {/* Checkboxes to select particular documents */}
                  <div className="mt-2 mb-3 bg-white p-3 rounded-xl border border-slate-200">
                    <span className="block text-[9px] uppercase font-bold text-slate-400 mb-2">Select Documents to Ask for Reupload:</span>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <label className="flex items-center gap-2 font-semibold text-slate-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={reuploadDocs.photo}
                          onChange={(e) => setReuploadDocs({ ...reuploadDocs, photo: e.target.checked })}
                          className="rounded text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                        />
                        Applicant Photo
                      </label>
                      <label className="flex items-center gap-2 font-semibold text-slate-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={reuploadDocs.aadhaar_document}
                          onChange={(e) => setReuploadDocs({ ...reuploadDocs, aadhaar_document: e.target.checked })}
                          className="rounded text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                        />
                        Aadhaar Document
                      </label>
                      {selectedApp.applicant_type === "player" && (
                        <label className="flex items-center gap-2 font-semibold text-slate-700 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={reuploadDocs.dob_proof}
                            onChange={(e) => setReuploadDocs({ ...reuploadDocs, dob_proof: e.target.checked })}
                            className="rounded text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                          />
                          DOB Proof
                        </label>
                      )}
                      <label className="flex items-center gap-2 font-semibold text-slate-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={reuploadDocs.main_document}
                          onChange={(e) => setReuploadDocs({ ...reuploadDocs, main_document: e.target.checked })}
                          className="rounded text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                        />
                        Main Evidence Document
                      </label>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-1 text-left">
                    <span className="text-[9px] uppercase font-bold text-slate-400 self-center mr-1">Suggested Templates:</span>
                    {getDynamicTemplates(selectedApp).map((tmpl, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleAppendTemplate(tmpl.text)}
                        className="px-2 py-0.5 rounded bg-white hover:bg-slate-100 text-[10px] text-slate-600 font-semibold border border-slate-200 transition-colors cursor-pointer select-none"
                      >
                        {tmpl.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    rows="2"
                    placeholder="E.g. Please upload your national medal certificate and age proof."
                    className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs text-slate-800 focus:outline-none focus:border-emerald-500"
                    value={requestedDocsDesc}
                    onChange={(e) => setRequestedDocsDesc(e.target.value)}
                  />
                  <div className="flex justify-end mt-1">
                    <button
                      type="button"
                      onClick={() => handleRequestDocs(selectedApp.id)}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs rounded-xl shadow-sm transition-all"
                      disabled={requestingDocs}
                    >
                      {requestingDocs ? "Sending Request..." : "Send Request & Notify"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Achievements & proofs JSON array */}
              <div>
                <h4 className="font-bold text-slate-900 mb-3 text-left">Achievements & Certificate Proofs Submitted</h4>
                
                {!selectedApp.achievements?.list || selectedApp.achievements.list.length === 0 ? (
                  <p className="text-slate-400 italic py-3 text-center border rounded-xl">No specific achievements added.</p>
                ) : (
                  <div className="space-y-4">
                    {selectedApp.achievements.list.map((ach, idx) => (
                      <div key={idx} className="bg-slate-50 border rounded-2xl p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-left">
                        <div className="space-y-1">
                          <div className="font-bold text-slate-900 text-sm">{ach.title}</div>
                          <div className="flex gap-4 text-[10px] text-slate-400 font-semibold">
                            <span>Level: <strong className="text-slate-600">{ach.level}</strong></span>
                            <span>Medal: <strong className="text-slate-600">{ach.medal}</strong></span>
                            {ach.date && <span>Date: <strong className="text-slate-600">{new Date(ach.date).toLocaleDateString()}</strong></span>}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                          {ach.file_path && (
                            <a
                              href={resolveAssetUrl(ach.file_path)}
                              target="_blank"
                              rel="noreferrer"
                              className="px-3 py-1.5 border bg-white rounded-lg text-[10px] font-bold text-slate-700 inline-flex items-center gap-1 hover:bg-slate-100 transition-colors"
                            >
                              <FaFileAlt /> View Certificate
                            </a>
                          )}

                          {/* Accept and Add to player profile button if registered player */}
                          {selectedApp.player_id && (
                            ach.status === "accepted" ? (
                              <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg font-bold text-[10px] inline-flex items-center gap-1">
                                <FaCheckCircle /> Imported
                              </span>
                            ) : (
                              <button
                                onClick={() => handleAcceptAchievement(selectedApp.id, idx)}
                                className="px-2.5 py-1.5 bg-yellow-500 text-slate-900 font-extrabold rounded-lg text-[10px] shadow hover:bg-yellow-400 transition-colors"
                              >
                                Accept & Import
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer Controls */}
            <div className="bg-slate-50 border-t px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-500">Update Application Status:</span>
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider border ${
                  selectedApp.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  selectedApp.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                  "bg-amber-50 text-amber-700 border-amber-200"
                }`}>
                  {selectedApp.status}
                </span>
              </div>

              <div className="flex gap-2">
                {selectedApp.status === "approved" ? (
                  <button
                    onClick={() => handleUpdateStatus(selectedApp.id, "pending")}
                    className="px-4 py-2 border border-amber-300 text-amber-700 bg-amber-50/30 rounded-xl hover:bg-amber-50 font-bold text-xs transition-colors"
                  >
                    Mark as Pending
                  </button>
                ) : (
                  <button
                    onClick={() => handleUpdateStatus(selectedApp.id, "approved")}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-bold text-xs transition-colors"
                  >
                    Approve Application <FaTrophy />
                  </button>
                )}
                {selectedApp.status !== "rejected" && (
                  <button
                    onClick={() => handleUpdateStatus(selectedApp.id, "rejected")}
                    className="px-4 py-2 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 font-bold text-xs transition-colors"
                  >
                    Reject Application
                  </button>
                )}
                {selectedApp.status === "rejected" && (
                  <button
                    onClick={() => handleUpdateStatus(selectedApp.id, "pending")}
                    className="px-4 py-2 border border-amber-300 text-amber-700 bg-amber-50/30 rounded-xl hover:bg-amber-50 font-bold text-xs transition-colors"
                  >
                    Mark as Pending
                  </button>
                )}
                <button
                  onClick={closeAppDetails}
                  className="px-4 py-2 border rounded-xl text-slate-600 hover:bg-slate-100 font-semibold text-xs transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* BULK DOCUMENT REQUEST MODAL */}
      {bulkRequestModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="font-extrabold text-base flex items-center gap-2">
                📩 Request Document Uploads (Bulk)
              </h3>
              <button
                onClick={() => setBulkRequestModalOpen(false)}
                className="text-slate-400 hover:text-white font-extrabold text-xl transition-colors"
              >
                &times;
              </button>
            </div>

            <div className="p-6 space-y-4 text-left text-xs">
              <div className="bg-slate-50 border rounded-2xl p-3 flex justify-between items-center">
                <span className="font-bold text-slate-600">Selected Applications:</span>
                <span className="px-2.5 py-1 bg-slate-200 text-slate-800 rounded-lg font-black">{selectedAppIds.length}</span>
              </div>

              {/* Inject Guidelines Toggle */}
              <label className="flex items-start gap-2.5 bg-emerald-50/50 border border-emerald-100 p-3 rounded-2xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkInjectInstructions}
                  onChange={(e) => setBulkInjectInstructions(e.target.checked)}
                  className="rounded text-emerald-600 border-gray-300 focus:ring-emerald-500 mt-0.5 cursor-pointer"
                />
                <div className="space-y-0.5">
                  <span className="block font-black text-slate-800">Auto-Inject Scanned Document Guidelines</span>
                  <span className="block text-[10px] text-slate-500 leading-normal font-semibold">
                    Automatically appends type-specific instructions based on applicant type (e.g. Merit certificates for players, trainee list for coaches, player details for districts).
                  </span>
                </div>
              </label>

              {/* Custom message with Font settings */}
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-2">
                  <span className="font-black text-slate-450 uppercase text-[9px] tracking-wider">Custom Message & Font Styling</span>
                  
                  {/* Font Settings Toolbar */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Font Family */}
                    <select
                      value={bulkFontFamily}
                      onChange={(e) => setBulkFontFamily(e.target.value)}
                      className="border rounded px-1.5 py-0.5 text-[10px] bg-white font-semibold text-slate-700"
                    >
                      <option value="sans-serif">Sans-Serif</option>
                      <option value="serif">Serif</option>
                      <option value="monospace">Monospace</option>
                    </select>

                    {/* Font Size */}
                    <select
                      value={bulkFontSize}
                      onChange={(e) => setBulkFontSize(e.target.value)}
                      className="border rounded px-1.5 py-0.5 text-[10px] bg-white font-semibold text-slate-700"
                    >
                      <option value="12px">Small (12px)</option>
                      <option value="14px">Normal (14px)</option>
                      <option value="18px">Large (18px)</option>
                      <option value="24px">XL (24px)</option>
                    </select>

                    {/* Font Weight */}
                    <button
                      type="button"
                      onClick={() => setBulkFontWeight(prev => prev === "bold" ? "normal" : "bold")}
                      className={`px-2 py-0.5 border rounded text-[10px] font-black transition-all ${
                        bulkFontWeight === "bold" ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:bg-slate-100 text-slate-700"
                      }`}
                    >
                      B
                    </button>

                    {/* Font Color preset pickers */}
                    <div className="flex items-center gap-1 border-l pl-2">
                      {[
                        { hex: "#334155", title: "Slate" },
                        { hex: "#059669", title: "Green" },
                        { hex: "#dc2626", title: "Red" },
                        { hex: "#4f46e5", title: "Indigo" },
                        { hex: "#d97706", title: "Orange" }
                      ].map(color => (
                        <button
                          key={color.hex}
                          type="button"
                          onClick={() => setBulkFontColor(color.hex)}
                          className={`w-3.5 h-3.5 rounded-full border transition-transform ${
                            bulkFontColor === color.hex ? "scale-125 border-slate-900 ring-1 ring-slate-400" : "border-slate-200 hover:scale-110"
                          }`}
                          style={{ backgroundColor: color.hex }}
                          title={color.title}
                        />
                      ))}
                      <input
                        type="color"
                        value={bulkFontColor}
                        onChange={(e) => setBulkFontColor(e.target.value)}
                        className="w-4 h-4 p-0 border rounded cursor-pointer bg-white"
                        title="Custom Color"
                      />
                    </div>

                  </div>
                </div>

                <textarea
                  rows="3"
                  value={bulkCustomMessage}
                  onChange={(e) => setBulkCustomMessage(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl p-3 text-xs focus:outline-none focus:border-slate-900 text-slate-800 placeholder-slate-400"
                  placeholder="Enter custom request message details..."
                />
              </div>

              {/* Portal Display Preview */}
              <div className="border rounded-2xl p-4 bg-slate-50 space-y-2">
                <span className="block text-[9px] uppercase font-black tracking-wider text-slate-450">Live Portal Display Preview</span>
                <div 
                  style={{
                    fontFamily: bulkFontFamily,
                    fontSize: bulkFontSize,
                    fontWeight: bulkFontWeight,
                    color: bulkFontColor,
                    lineHeight: "1.5"
                  }}
                  className="bg-white border rounded-xl p-3 whitespace-pre-line text-left shadow-sm max-h-[120px] overflow-y-auto"
                >
                  {bulkCustomMessage}
                  {bulkInjectInstructions && (
                    <div className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500 italic font-semibold">
                      [Applicant-type specific scanned documents guidelines will be dynamically injected here, e.g. "Player should upload all the merit certificates...", etc.]
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setBulkRequestModalOpen(false)}
                  className="px-4 py-2 border rounded-xl text-slate-600 hover:bg-slate-100 font-bold text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendBulkRequest}
                  disabled={submittingBulkRequest}
                  className="px-5 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-black text-xs transition-colors shadow-sm"
                >
                  {submittingBulkRequest ? "Sending Requests..." : "🚀 Send Request & Notify"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTP MODAL FOR UNLOCKING RANKINGS */}
      {otpModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="font-extrabold text-base flex items-center gap-2">
                🔒 Security Verification Required
              </h3>
              <button
                onClick={() => setOtpModalOpen(false)}
                className="text-slate-400 hover:text-white font-extrabold text-xl transition-colors"
              >
                &times;
              </button>
            </div>
            
            <div className="p-6 space-y-4 text-left text-xs">
              <p className="text-slate-600 leading-relaxed font-semibold">
                An email OTP verification code has been sent to your email address:
                <strong className="block text-slate-800 mt-1 font-extrabold">{user?.email || "N/A"}</strong>
              </p>
              
              <div>
                <label className="block text-[10px] uppercase font-black text-slate-400 mb-1">Enter 6-Digit Email OTP</label>
                <input
                  type="text"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").substring(0, 6))}
                  placeholder="e.g. 123456"
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:outline-none focus:border-slate-850 font-black text-center tracking-widest text-lg"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setOtpModalOpen(false)}
                  className="px-4 py-2 border rounded-xl text-slate-600 hover:bg-slate-100 font-bold text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleVerifyOtpAndUnlock}
                  className="px-5 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-black text-xs transition-colors"
                >
                  Verify & Unlock
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExcellenceAwardsPoster show={showPosterModal} onClose={() => setShowPosterModal(false)} />

      {/* Lightbox Modal for Media Preview */}
      {expandedMedia && (
        <div
          className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 cursor-pointer animate-in fade-in duration-200"
          onClick={() => setExpandedMedia(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh] bg-white rounded-3xl overflow-hidden shadow-2xl p-2 cursor-default" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setExpandedMedia(null)}
              className="absolute top-4 right-4 bg-slate-900/80 hover:bg-slate-900 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg cursor-pointer transition-all z-10"
            >
              &times;
            </button>
            {expandedMedia.type === 'video' ? (
              <video
                src={expandedMedia.url}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] object-contain rounded-2xl"
              />
            ) : (
              <img
                src={expandedMedia.url}
                alt="Expanded Preview"
                className="max-w-full max-h-[85vh] object-contain rounded-2xl"
              />
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ManageExcellenceAwards;
